import * as React from 'react';
import { VatReportData } from 'store/vat/models';
import { TaxReportSubsectionDataResponse } from 'store/vat/models/vatReportData';
import { getRawNumberForCopy, useNumberFormat } from 'utils/numberFormat';

interface SouthAfricaFormProps {
  data?: VatReportData;
  renderValueCell: (boxNumber: string) => void;
  finalTotals: boolean;
}

function normalizeBoxId(code: string | undefined): string {
  if (!code) return '';
  return code.replace(/^Box\s+/, '').replace(/\s/g, '').toLowerCase();
}

export const SouthAfricaForm: React.FC<SouthAfricaFormProps> = ({
  data,
  renderValueCell,
  finalTotals,
}) => {
  const { formatNumber } = useNumberFormat();
  React.useEffect(() => {
    const selectedSubsections: TaxReportSubsectionDataResponse[] | undefined =
      !finalTotals ? data?.subsections : data?.adjustedSubsections;
    if (data === undefined || data === null) return;

    const currencySignSpans = document.querySelectorAll('.currency-sign');
    currencySignSpans.forEach((currencySignSpan) => {
      if (data.currencySymbol !== null) {
        currencySignSpan.textContent = data.currencySymbol;
      }
    });

    data.sections?.forEach((section, i) => {
      const sectionNameCell = document.querySelector(`#section10${i}-name`);
      if (sectionNameCell) {
        sectionNameCell.textContent = section.name;
      }
    });

    selectedSubsections?.forEach((subsection) => {
      const boxId = normalizeBoxId(subsection.code);
      const subsectionNameCell = document.getElementById(`subsection${boxId}-name`);
      const subsectionBoxNameCell = document.getElementById(`subsection${boxId}-box-name`);
      const subsectionValueSpan = document.getElementById(`subsection${boxId}-value-span`);

      if (subsectionNameCell) {
        subsectionNameCell.textContent = subsection.name;
      }
      if (subsectionBoxNameCell !== null && subsectionValueSpan !== null) {
        subsectionBoxNameCell.textContent = subsection.code;
        const textContent: any = subsection.net;
        subsectionValueSpan.textContent = formatNumber(textContent);
        subsectionValueSpan.setAttribute('data-raw-value', getRawNumberForCopy(textContent));
      }
    });
  }, [data, finalTotals, formatNumber]);

  return (
    <>
      {/* Section A: Calculation of Output tax and Imported Services */}
      <table className="vatreporttable table">
        <thead>
          <tr>
            <th id="section100-name">A. Calculation of Output tax and Imported Services</th>
            <th></th>
            <th style={{ width: 110, textAlign: 'center' }}>
              Amount - <span className="currency-sign"></span>
            </th>
            <th></th>
            <th style={{ width: 110, textAlign: 'center' }}>
              Amount - <span className="currency-sign"></span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td id="subsection1-name">Standard rate (Excluding capital goods and/or services and accommodation)</td>
            <td id="subsection1-box-name" className="box">Box 1</td>
            <td id="subsection1-value" className="rightalign value-field">{renderValueCell('1')}</td>
            <td id="subsection4-box-name" className="box">Box 4</td>
            <td id="subsection4-value" className="rightalign value-field">{renderValueCell('4')}</td>
          </tr>
          <tr>
            <td id="subsection1a-name">Standard rate (only capital goods and/or services)</td>
            <td id="subsection1a-box-name" className="box">Box 1A</td>
            <td id="subsection1a-value" className="rightalign value-field">{renderValueCell('1a')}</td>
            <td id="subsection4a-box-name" className="box">Box 4A</td>
            <td id="subsection4a-value" className="rightalign value-field">{renderValueCell('4a')}</td>
          </tr>
          <tr>
            <td id="subsection2-name">Zero rate (excluding goods exported)</td>
            <td id="subsection2-box-name" className="box">Box 2</td>
            <td id="subsection2-value" className="rightalign value-field">{renderValueCell('2')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection2a-name">Zero rate (only goods exported)</td>
            <td id="subsection2a-box-name" className="box">Box 2A</td>
            <td id="subsection2a-value" className="rightalign value-field">{renderValueCell('2a')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection3-name">Exempt and non-supplies</td>
            <td id="subsection3-box-name" className="box">Box 3</td>
            <td id="subsection3-value" className="rightalign value-field">{renderValueCell('3')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection5-name" className="subtotal">Supply of accommodation - Exceeding 28 days</td>
            <td id="subsection5-box-name" className="box">Box 5</td>
            <td id="subsection5-value" className="rightalign value-field">{renderValueCell('5')}</td>
            <td id="subsection6-box-name" className="box">Box 6</td>
            <td id="subsection6-value" className="rightalign value-field">{renderValueCell('6')}</td>
          </tr>
          <tr>
            <td id="subsection7-name">Value Not exceeding 28 Days</td>
            <td id="subsection7-box-name" className="box">Box 7</td>
            <td id="subsection7-value" className="rightalign value-field">{renderValueCell('7')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection8-name" className="subtotal">Sub-Total (total of boxes 6 and 7)</td>
            <td id="subsection8-box-name" className="box">Box 8</td>
            <td id="subsection8-value" className="rightalign">{renderValueCell('8')}</td>
            <td id="subsection9-box-name" className="box">Box 9</td>
            <td id="subsection9-value" className="rightalign value-field">{renderValueCell('9')}</td>
          </tr>
          <tr>
            <td id="subsection10-name">Change in use and export of second-hand goods</td>
            <td id="subsection10-box-name" className="box">Box 10</td>
            <td id="subsection10-value" className="rightalign value-field">{renderValueCell('10')}</td>
            <td id="subsection11-box-name" className="box">Box 11</td>
            <td id="subsection11-value" className="rightalign value-field">{renderValueCell('11')}</td>
          </tr>
          <tr>
            <td id="subsection12-name">Other and imported services</td>
            <td id="subsection12-box-name" className="box">Box 12</td>
            <td id="subsection12-value" className="rightalign value-field">{renderValueCell('12')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection13-name" className="subtotal-2" colSpan={3}>Total A: Total Output tax (4 + 4A + 9 + 11 + 12)</td>
            <td id="subsection13-box-name" className="box">Box 13</td>
            <td id="subsection13-value" className="rightalign value-field">{renderValueCell('13')}</td>
          </tr>
        </tbody>
      </table>

      <div className="divide-20" />

      {/* Section B: Calculation of Input tax */}
      <table className="vatreporttable table">
        <thead>
          <tr>
            <th id="section101-name">B. Calculation of Input tax</th>
            <th></th>
            <th style={{ width: 110, textAlign: 'center' }}>
              Amount - <span className="currency-sign"></span>
            </th>
            <th></th>
            <th style={{ width: 110, textAlign: 'center' }}>
              Amount - <span className="currency-sign"></span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td id="subsection14-name">Capital goods and/or services supplied to you</td>
            <td id="subsection14-box-name" className="box">Box 14</td>
            <td id="subsection14-value" className="rightalign value-field">{renderValueCell('14')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection14a-name">Capital goods imported by you</td>
            <td id="subsection14a-box-name" className="box">Box 14A</td>
            <td id="subsection14a-value" className="rightalign value-field">{renderValueCell('14a')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection15-name">Other goods and/or services supplied to you (not capital goods)</td>
            <td id="subsection15-box-name" className="box">Box 15</td>
            <td id="subsection15-value" className="rightalign value-field">{renderValueCell('15')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection15a-name">Other goods imported by you (not capital goods)</td>
            <td id="subsection15a-box-name" className="box">Box 15A</td>
            <td id="subsection15a-value" className="rightalign value-field">{renderValueCell('15a')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection16-name" className="subtotal">Adjustments - Change in use</td>
            <td id="subsection16-box-name" className="box">Box 16</td>
            <td id="subsection16-value" className="rightalign value-field">{renderValueCell('16')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection17-name">Bad Debts</td>
            <td id="subsection17-box-name" className="box">Box 17</td>
            <td id="subsection17-value" className="rightalign value-field">{renderValueCell('17')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection18-name">Other</td>
            <td id="subsection18-box-name" className="box">Box 18</td>
            <td id="subsection18-value" className="rightalign value-field">{renderValueCell('18')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection19-name" className="subtotal-2" colSpan={3}>Total B: Total Input Tax (14 + 14A + 15 + 15A + 16 + 17 + 18)</td>
            <td id="subsection19-box-name" className="box">Box 19</td>
            <td id="subsection19-value" className="rightalign value-field">{renderValueCell('19')}</td>
          </tr>
          <tr>
            <td colSpan={5} className="nbl nbt nbr space20"></td>
          </tr>
          <tr>
            <td id="subsection20-name" className="subtotal-2" colSpan={3}>Total VAT Payable/Refundable (Total A - Total B)</td>
            <td id="subsection20-box-name" className="box">Box 20</td>
            <td id="subsection20-value" className="rightalign value-field">{renderValueCell('20')}</td>
          </tr>
        </tbody>
      </table>

      <div className="divide-20" />

      {/* Section C: Calculation of Diesel Refund */}
      <table className="vatreporttable table">
        <thead>
          <tr>
            <th id="section102-name">C. Calculation of Diesel Refund in terms of the Customs and Excise Tax</th>
            <th></th>
            <th style={{ width: 110, textAlign: 'center' }}>
              Amount - <span className="currency-sign"></span>
            </th>
            <th></th>
            <th style={{ width: 110, textAlign: 'center' }}>
              Amount - <span className="currency-sign"></span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td id="subsection21-name" className="subtotal">Onland - Total Purchases used (I)</td>
            <td id="subsection21-box-name" className="box">Box 21</td>
            <td id="subsection21-value" className="rightalign value-field">{renderValueCell('21')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection22-name">Non-Eligible Purchases used (I)</td>
            <td id="subsection22-box-name" className="box">Box 22</td>
            <td id="subsection22-value" className="rightalign value-field">{renderValueCell('22')}</td>
            <td id="subsection23-box-name" className="box">Box 23</td>
            <td id="subsection23-value" className="rightalign value-field">{renderValueCell('23')}</td>
          </tr>
          <tr>
            <td id="subsection24-name">Eligible Purchases used (I) - Claimable liters</td>
            <td id="subsection24-box-name" className="box">Box 24</td>
            <td id="subsection24-value" className="rightalign value-field">{renderValueCell('24')}</td>
            <td id="subsection25-box-name" className="box">Box 25</td>
            <td id="subsection25-value" className="rightalign value-field">{renderValueCell('25')}</td>
          </tr>
          <tr>
            <td id="subsection26-name" className="subtotal">Offshore - Total Purchases used (I)</td>
            <td id="subsection26-box-name" className="box">Box 26</td>
            <td id="subsection26-value" className="rightalign value-field">{renderValueCell('26')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection27-name">Non-Eligible Purchases used (I)</td>
            <td id="subsection27-box-name" className="box">Box 27</td>
            <td id="subsection27-value" className="rightalign value-field">{renderValueCell('27')}</td>
            <td id="subsection28-box-name" className="box">Box 28</td>
            <td id="subsection28-value" className="rightalign value-field">{renderValueCell('28')}</td>
          </tr>
          <tr>
            <td id="subsection29-name">Eligible Purchases - Claimable</td>
            <td id="subsection29-box-name" className="box">Box 29</td>
            <td id="subsection29-value" className="rightalign value-field">{renderValueCell('29')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection30-name" className="subtotal">Rail & Harbour - Total Purchases used (I)</td>
            <td id="subsection30-box-name" className="box">Box 30</td>
            <td id="subsection30-value" className="rightalign value-field">{renderValueCell('30')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection31-name">Non-Eligible Purchases used (I)</td>
            <td id="subsection31-box-name" className="box">Box 31</td>
            <td id="subsection31-value" className="rightalign value-field">{renderValueCell('31')}</td>
            <td id="subsection32-box-name" className="box">Box 32</td>
            <td id="subsection32-value" className="rightalign value-field">{renderValueCell('32')}</td>
          </tr>
          <tr>
            <td id="subsection33-name">Eligible Purchases - Claimable</td>
            <td id="subsection33-box-name" className="box">Box 33</td>
            <td id="subsection33-value" className="rightalign value-field">{renderValueCell('33')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td id="subsection34-name" className="subtotal">Peak Power Plant - Total Purchases used (I)</td>
            <td id="subsection34-box-name" className="box">Box 34</td>
            <td id="subsection34-value" className="rightalign value-field">{renderValueCell('34')}</td>
            <td className="nbr nbb"></td>
            <td className="nbb"></td>
          </tr>
          <tr>
            <td id="subsection35-name">Non-Eligible Purchases used (I)</td>
            <td id="subsection35-box-name" className="box">Box 35</td>
            <td id="subsection35-value" className="rightalign value-field">{renderValueCell('35')}</td>
            <td id="subsection36-box-name" className="box">Box 36</td>
            <td id="subsection36-value" className="rightalign value-field">{renderValueCell('36')}</td>
          </tr>
          <tr>
            <td id="subsection37-name">Eligible Purchases - Claimable</td>
            <td id="subsection37-box-name" className="box">Box 37</td>
            <td id="subsection37-value" className="rightalign value-field">{renderValueCell('37')}</td>
            <td className="nbr"></td>
            <td></td>
          </tr>
          <tr>
            <td colSpan={5} className="nbl nbt nbr space20"></td>
          </tr>
          <tr>
            <td id="subsection38-name" className="subtotal-2" colSpan={3}>Total Amount Payable/Refundable 20 - (25 + 29 + 33 + 37) or 20 + (25 + 29 + 33 + 37)</td>
            <td id="subsection38-box-name" className="box">Box 38</td>
            <td id="subsection38-value" className="rightalign value-field">{renderValueCell('38')}</td>
          </tr>
        </tbody>
      </table>
    </>
  );
};
